'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import katex from 'katex';
import type { BoardEntry, CoordinatePlanePayload, GeometryPayload, NumberLinePayload } from './types';
import { StarRewardOverlay } from './StarRewardOverlay';
import { NumberLineEntry } from './NumberLineEntry';
import { CoordinatePlaneEntry } from './CoordinatePlaneEntry';
import { GeometryEntry } from './GeometryEntry';
import {
  appendMixedTextNodes,
  appendTextWithInlineItalicsAndLineBreaks,
  isLikelyHtmlFragment,
  normalizeLineBreaks,
  renderMixedContent,
} from './textFormatting';
import { withChoiceLabel } from '@/lib/liveTutorOptionLabel';

type StarState = 'pending' | 'success' | 'fail';

type ImageLayout = 'multi_diagram' | 'option_images' | 'diagram_plus_options' | 'options_in_image';

interface BoardCanvasProps {
  entries: BoardEntry[];
  // Problem mode header
  question?: string;
  questionHtml?: string;
  options?: string[];
  questionImage?: string; // Optional diagram/figure below problem options
  questionImages?: string[]; // All diagram/figure image URLs
  questionImageLayout?: ImageLayout;
  // Key Property mode header
  headerTitle?: string;
  headerImage?: string;
  headerImagePosition?: 'below' | 'right';
  // Layout
  width?: number;
  height?: number;
  /** Scale factor for text density tuning (used for mobile compact layout). */
  fontScale?: number;
  /** Enables tighter spacing and smaller text for compact/mobile boards. */
  compactMode?: boolean;
  className?: string;
  /** Inline styles applied to the board surface (useful for overriding board colors). */
  style?: React.CSSProperties;
  // Star system props
  starStates?: StarState[];
  highlightStarIndex?: number | null;
  onStarStatesChange?: (states: StarState[]) => void;
}

/**
 * Renders board entries as a vertical scrolling chalkboard.
 * Reuses V1's chalkboard-theme CSS from globals.css.
 */
export function BoardCanvas({
  entries,
  question,
  questionHtml,
  options,
  questionImage,
  questionImages,
  questionImageLayout,
  headerTitle,
  headerImage,
  headerImagePosition,
  width,
  height = 600,
  fontScale = 1,
  compactMode = false,
  className = '',
  style: externalStyle,
  starStates: externalStarStates,
  highlightStarIndex: externalHighlightIndex
}: BoardCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);
  const isCompact = compactMode;
  const effectiveFontScale = isCompact
    ? Math.max(0.72, Math.min(fontScale, 1))
    : 1;
  const contentPaddingX = isCompact ? 16 : 32;
  const contentPaddingTop = isCompact ? 16 : 40;
  const contentPaddingBottom = isCompact ? 18 : 32;

  const internalStarStates = useMemo<StarState[]>(() => ['pending', 'pending', 'pending'], []);
  const starStates = externalStarStates ?? internalStarStates;
  const highlightStarIndex = externalHighlightIndex ?? null;

  // Auto-scroll to bottom when new entries appear
  useEffect(() => {
    if (entries.length > prevCountRef.current) {
      const container = containerRef.current;
      if (container) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }
    }
    prevCountRef.current = entries.length;
  }, [entries.length]);

  return (
    <div
      className={`whiteboard-canvas chalkboard-theme border-0 rounded-2xl shadow-lg relative overflow-hidden ${className}`}
      style={{ width, height, ...externalStyle }}
    >
      {/* Scrollable content area */}
      <div
        ref={containerRef}
        className="whiteboard-scroll-container relative overflow-y-auto overflow-x-hidden h-full"
      >
        <div
          className="whiteboard-content relative flex flex-col min-h-full"
          style={{
            paddingLeft: `${contentPaddingX}px`,
            paddingRight: `${contentPaddingX}px`,
            paddingTop: `${contentPaddingTop}px`,
            paddingBottom: `${contentPaddingBottom}px`,
          }}
        >
          {/* Header: either Problem question or Concept title+image */}
          {question && (
            <QuestionHeader
              question={question}
              questionHtml={questionHtml}
              options={options}
              questionImage={questionImage}
              questionImages={questionImages}
              imageLayout={questionImageLayout}
              fontScale={effectiveFontScale}
              compactMode={isCompact}
            />
          )}
          {!question && headerTitle && (
            <ConceptHeader
              title={headerTitle}
              image={headerImage}
              imagePosition={headerImagePosition}
              fontScale={effectiveFontScale}
              compactMode={isCompact}
            />
          )}

          {entries.length === 0 && !question && !headerTitle && (
            <div
              className="flex items-center justify-center flex-1 text-sm italic"
              style={{ color: 'var(--wb-chalk-soft, #dbd0b4)', opacity: 0.4 }}
            >
              Waiting for tutor to write...
            </div>
          )}

          {(() => {
            let currentSection: 'heading' | null = null;
            return entries.map((entry, i) => {
              const inHeadingSection = currentSection === 'heading' && entry.type !== 'heading';
              if (entry.type === 'heading') currentSection = 'heading';
              else if (entry.type === 'question') currentSection = null;
              // subheading does NOT reset currentSection — it lives inside a heading section
              return (
                <EntryItem
                  key={entry.id}
                  entry={entry}
                  prevType={i > 0 ? entries[i - 1].type : undefined}
                  isFirst={i === 0}
                  inHeadingSection={inHeadingSection}
                  fontScale={effectiveFontScale}
                  compactMode={isCompact}
                />
              );
            });
          })()}

          <div ref={bottomRef} />
        </div>
      </div>

      {/* Star reward overlay - outside scroll container, inside outer wrapper */}
      <StarRewardOverlay starStates={starStates} highlightIndex={highlightStarIndex} />
    </div>
  );
}

function QuestionHeader({
  question,
  questionHtml,
  options,
  questionImage,
  questionImages,
  imageLayout,
  fontScale,
  compactMode,
}: {
  question: string;
  questionHtml?: string;
  options?: string[];
  questionImage?: string;
  questionImages?: string[];
  imageLayout?: ImageLayout;
  fontScale: number;
  compactMode: boolean;
}) {
  const elRef = useRef<HTMLDivElement>(null);
  const [failedImgs, setFailedImgs] = useState<Set<string>>(new Set());
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [snapshotHeight, setSnapshotHeight] = useState<number>(0);
  const snapshotCleanupRef = useRef<(() => void) | null>(null);
  const trimmedQuestionHtml = questionHtml?.trim();
  const snapshotHtml = useMemo(() => {
    if (!trimmedQuestionHtml || !isLikelyHtmlFragment(trimmedQuestionHtml)) {
      return undefined;
    }
    return trimmedQuestionHtml;
  }, [trimmedQuestionHtml]);
  const fallbackQuestionText = snapshotHtml ? question : (trimmedQuestionHtml || question);

  const markFailed = (src: string) => setFailedImgs((prev) => new Set(prev).add(src));

  useEffect(() => {
    if (snapshotHtml) {
      const contentEl = elRef.current?.querySelector('.question-header-content');
      if (contentEl) {
        contentEl.innerHTML = '';
      }
      return;
    }

    const contentEl = elRef.current?.querySelector('.question-header-content');
    if (!contentEl) return;
    const normalizedQuestion = normalizeLineBreaks(fallbackQuestionText);

    if (normalizedQuestion.includes('$')) {
      (contentEl as HTMLElement).style.whiteSpace = 'normal';
      contentEl.innerHTML = renderMixedContent(normalizedQuestion);
    } else {
      (contentEl as HTMLElement).style.whiteSpace = 'pre-wrap';
      contentEl.innerHTML = '';
      appendTextWithInlineItalicsAndLineBreaks(contentEl as HTMLElement, normalizedQuestion);
    }
  }, [fallbackQuestionText, snapshotHtml]);

  // Render MCQ options below the question
  const optionsRef = useRef<HTMLDivElement>(null);
  // Hide text options when images ARE the options
  const hideTextOptions = imageLayout === 'option_images' || imageLayout === 'diagram_plus_options';

  useEffect(() => {
    const container = optionsRef.current;
    if (!container || !options || options.length === 0 || hideTextOptions) {
      if (container) container.innerHTML = '';
      return;
    }
    container.innerHTML = '';

    const choiceLens = options.map((o) => o.length);
    const maxChoiceLen = Math.max(...choiceLens);
    const shortCount = choiceLens.filter((l) => l <= 8).length;
    const useRow = maxChoiceLen <= 20 || shortCount >= options.length - 1;
    const useGrid = !useRow && maxChoiceLen <= 50 && options.length === 4;

    container.className = 'whiteboard-options mt-4';

    container.style.marginTop = compactMode ? '12px' : '20px';
    container.style.textAlign = 'left';

    if (useRow) {
      container.style.display = 'inline-flex';
      container.style.flexDirection = 'row';
      container.style.flexWrap = 'wrap';
      container.style.justifyContent = 'center';
      container.style.gap = compactMode ? '6px 18px' : '8px 32px';
    } else if (useGrid) {
      container.style.display = 'inline-grid';
      container.style.gridTemplateColumns = 'auto auto';
      container.style.gap = compactMode ? '8px 24px' : '12px 60px';
    } else {
      container.style.display = 'inline-flex';
      container.style.flexDirection = 'column';
      container.style.gap = compactMode ? '6px' : '8px';
    }

    const labels = ['A', 'B', 'C', 'D', 'E', 'F'];
    options.forEach((opt, i) => {
      const optionDiv = document.createElement('div');
      optionDiv.className = 'whiteboard-option';
      optionDiv.style.whiteSpace = 'normal';

      const optionText = withChoiceLabel(opt, i, labels);
      appendMixedTextNodes(optionDiv, optionText);

      container.appendChild(optionDiv);
    });
  }, [compactMode, options, hideTextOptions]);

  // Determine which images to render and how
  const allImages = questionImages ?? (questionImage ? [questionImage] : []);
  const layout = imageLayout ?? (allImages.length <= 1 ? undefined : undefined);
  // For default and multi_diagram, show images between question text and options
  const imageBeforeOptions = !layout || layout === 'multi_diagram';
  const shouldRenderFallbackAssets = !snapshotHtml;
  const syncSnapshotHeight = React.useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.isConnected) {
      console.warn('[BoardCanvas] syncSnapshotHeight skipped: iframe missing or disconnected');
      return;
    }

    const frameDoc = iframe.contentDocument;
    if (!frameDoc) {
      console.warn('[BoardCanvas] syncSnapshotHeight skipped: iframe document missing');
      return;
    }

    const docEl = frameDoc.documentElement;
    const bodyEl = frameDoc.body;
    if (!docEl && !bodyEl) {
      console.warn('[BoardCanvas] syncSnapshotHeight skipped: documentElement and body missing');
      return;
    }

    const nextHeight = Math.ceil(
      Math.max(
        docEl?.scrollHeight ?? 0,
        bodyEl?.scrollHeight ?? 0,
      ),
    );
    if (nextHeight > 0) {
      setSnapshotHeight(nextHeight);
    }
  }, []);

  const attachSnapshotObservers = React.useCallback(() => {
    snapshotCleanupRef.current?.();
    snapshotCleanupRef.current = null;

    const iframe = iframeRef.current;
    const frameDoc = iframe?.contentDocument;
    const frameWindow = iframe?.contentWindow;
    if (!iframe || !frameDoc || !frameWindow) return;

    const cleanupFns: Array<() => void> = [];
    const requestSync = () => {
      if (!iframeRef.current?.isConnected) {
        console.warn('[BoardCanvas] requestSync skipped: iframe disconnected');
        return;
      }
      frameWindow.requestAnimationFrame(() => {
        if (!iframeRef.current?.isConnected) {
          console.warn('[BoardCanvas] requestAnimationFrame sync skipped: iframe disconnected');
          return;
        }
        syncSnapshotHeight();
      });
    };

    requestSync();

    const observedNodes = [frameDoc.documentElement, frameDoc.body].filter(
      Boolean,
    ) as Element[];

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        requestSync();
      });
      observedNodes.forEach((node) => resizeObserver.observe(node));
      cleanupFns.push(() => resizeObserver.disconnect());
    }

    const mutationObserver = new MutationObserver(() => {
      requestSync();
    });
    observedNodes.forEach((node) => {
      mutationObserver.observe(node, {
        childList: true,
        subtree: true,
        attributes: true,
        characterData: true,
      });
    });
    cleanupFns.push(() => mutationObserver.disconnect());

    const images = Array.from(frameDoc.images);
    images.forEach((img) => {
      img.addEventListener('load', requestSync);
      img.addEventListener('error', requestSync);
      cleanupFns.push(() => {
        img.removeEventListener('load', requestSync);
        img.removeEventListener('error', requestSync);
      });
    });

    frameWindow.addEventListener('resize', requestSync);
    cleanupFns.push(() => frameWindow.removeEventListener('resize', requestSync));

    snapshotCleanupRef.current = () => {
      cleanupFns.forEach((cleanup) => cleanup());
    };
  }, [syncSnapshotHeight]);

  useEffect(() => {
    if (!snapshotHtml) {
      snapshotCleanupRef.current?.();
      snapshotCleanupRef.current = null;
      setSnapshotHeight(0);
      return;
    }

    syncSnapshotHeight();
    const timer = window.setTimeout(() => {
      attachSnapshotObservers();
      syncSnapshotHeight();
    }, 120);
    return () => window.clearTimeout(timer);
  }, [attachSnapshotObservers, snapshotHtml, syncSnapshotHeight]);

  useEffect(() => {
    return () => {
      snapshotCleanupRef.current?.();
      snapshotCleanupRef.current = null;
    };
  }, []);

  const imgStyle: React.CSSProperties = {
    maxHeight: compactMode ? '150px' : '200px',
    maxWidth: compactMode ? '300px' : '360px',
    objectFit: 'contain',
    borderRadius: '4px',
  };

  const renderImages = () => {
    if (allImages.length === 0) return null;

    // multi_diagram: all images stacked vertically
    if (layout === 'multi_diagram') {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
          {allImages.map((src) =>
            failedImgs.has(src) ? null : (
              <img key={src} src={src} alt="Problem diagram" onError={() => markFailed(src)}
                style={{
                  ...imgStyle,
                  maxHeight: compactMode ? '130px' : '160px',
                  maxWidth: compactMode ? '170px' : '200px',
                }} />
            )
          )}
        </div>
      );
    }

    // option_images: images in a labeled grid (A/B/C/D/E)
    if (layout === 'option_images') {
      const labels = ['A', 'B', 'C', 'D', 'E'];
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', marginTop: '16px' }}>
          {allImages.map((src, i) =>
            failedImgs.has(src) ? null : (
              <div key={src} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.75em', opacity: 0.7, marginBottom: '4px' }}>{labels[i] ?? ''}</div>
                <img src={src} alt={`Option ${labels[i] ?? i + 1}`} onError={() => markFailed(src)}
                  style={{
                    ...imgStyle,
                    maxHeight: compactMode ? '96px' : '120px',
                    maxWidth: compactMode ? '120px' : '140px',
                  }} />
              </div>
            )
          )}
        </div>
      );
    }

    // diagram_plus_options: first image as diagram, rest as labeled option images
    if (layout === 'diagram_plus_options') {
      const [diagram, ...optionImgs] = allImages;
      const labels = ['A', 'B', 'C', 'D', 'E'];
      return (
        <>
          {diagram && !failedImgs.has(diagram) && (
            <img src={diagram} alt="Problem diagram" onError={() => markFailed(diagram)}
              style={{ ...imgStyle, margin: '16px auto 0', display: 'block' }} />
          )}
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', marginTop: '12px' }}>
            {optionImgs.map((src, i) =>
              failedImgs.has(src) ? null : (
                <div key={src} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '0.75em', opacity: 0.7, marginBottom: '4px' }}>{labels[i] ?? ''}</div>
                  <img src={src} alt={`Option ${labels[i] ?? i + 1}`} onError={() => markFailed(src)}
                    style={{
                      ...imgStyle,
                      maxHeight: compactMode ? '96px' : '120px',
                      maxWidth: compactMode ? '120px' : '140px',
                    }} />
                </div>
              )
            )}
          </div>
        </>
      );
    }

    // options_in_image: first image as diagram, second as wide options figure
    if (layout === 'options_in_image') {
      return (
        <div style={{ marginTop: '16px' }}>
          {allImages.map((src, i) =>
            failedImgs.has(src) ? null : (
              <img key={src} src={src} alt={i === 0 ? 'Problem diagram' : 'Answer options'}
                onError={() => markFailed(src)}
                style={{
                  ...imgStyle,
                  maxWidth: i === 0 ? (compactMode ? '300px' : '360px') : '100%',
                  margin: i === 0 ? '0 auto' : '12px auto 0',
                  display: 'block',
                }} />
            )
          )}
        </div>
      );
    }

    // Default single image
    const src = allImages[0];
    if (!src || failedImgs.has(src)) return null;
    return (
      <img src={src} alt="Problem diagram" onError={() => markFailed(src)}
        style={{ ...imgStyle, margin: '16px auto 0', display: 'block' }} />
    );
  };

  return (
    <div
      ref={elRef}
      className="whiteboard-element text-center"
      style={{
        fontSize: `${(compactMode ? 1.2 : 1.35) * fontScale}em`,
        lineHeight: compactMode ? '1.55' : '1.72',
        paddingBottom: compactMode ? '0.5rem' : '0.75rem',
        borderBottom: '2px solid rgba(241,233,208,0.25)',
      }}
    >
      <div className="question-header-content" />
      {snapshotHtml ? (
        <iframe
          ref={iframeRef}
          title="Imported question snapshot"
          scrolling="no"
          srcDoc={buildQuestionSnapshotDocument(snapshotHtml)}
          onLoad={() => {
            attachSnapshotObservers();
            syncSnapshotHeight();
          }}
          style={{
            display: 'block',
            width: '100%',
            height: `${Math.max(snapshotHeight, compactMode ? 140 : 180)}px`,
            marginTop: '14px',
            border: '0',
            background: 'transparent',
          }}
        />
      ) : null}
      {shouldRenderFallbackAssets && imageBeforeOptions && renderImages()}
      {shouldRenderFallbackAssets && options && options.length > 0 && !hideTextOptions && <div ref={optionsRef} />}
      {shouldRenderFallbackAssets && !imageBeforeOptions && renderImages()}
    </div>
  );
}

function buildQuestionSnapshotDocument(questionHtml: string): string {
  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <style>
      :root {
        color-scheme: light;
      }
      * {
        box-sizing: border-box;
      }
      html,
      body {
        margin: 0;
        padding: 0;
        background: transparent;
      }
      body {
        color: #f3ecd7;
        overflow: hidden;
        display: block;
        width: 100%;
        font-family: "Chalkboard SE", "Marker Felt", "Trebuchet MS", "Avenir Next", sans-serif;
        line-height: 1.62;
        letter-spacing: 0.005em;
        font-size: 1.01rem;
        font-weight: 500;
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
      }
      p,
      div,
      span,
      li,
      button,
      legend,
      label {
        font-family: inherit;
        color: inherit;
      }
      body * {
        color: inherit !important;
        font-family: inherit !important;
        letter-spacing: inherit !important;
        text-shadow: none !important;
      }
      .sr-only {
        display: none !important;
      }
      strong,
      b {
        color: #f8f1d8 !important;
        font-weight: 700 !important;
      }
      [data-perseus-paragraph-index] {
        margin-bottom: 16px;
      }
      .paragraph {
        color: #f0e6c8 !important;
        line-height: 1.62 !important;
        letter-spacing: 0.005em !important;
      }
      img,
      svg {
        max-width: 100%;
      }
      ul[role="list"] {
        list-style: none;
        margin: 14px 0 0;
        padding: 0;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
      li[role="listitem"] {
        display: grid !important;
        grid-template-columns: 52px minmax(0, 1fr);
        gap: 12px;
        align-items: start;
        padding: 8px 12px;
        border-radius: 14px;
        background: rgba(255, 255, 255, 0.03);
        border: 1px solid rgba(241, 233, 208, 0.1);
      }
      button[aria-pressed] {
        width: 42px !important;
        height: 42px !important;
        min-width: 42px !important;
        min-height: 42px !important;
        border-radius: 999px !important;
        border: 2px solid rgba(241, 233, 208, 0.35) !important;
        background: rgba(255, 255, 255, 0.02) !important;
        color: #f3ecd7 !important;
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-weight: 700 !important;
        padding: 0 !important;
        box-shadow: none !important;
      }
      button[aria-pressed="true"] {
        border-color: rgba(245, 222, 179, 0.95) !important;
        background: rgba(255, 248, 220, 0.1) !important;
        color: #fff6d6 !important;
      }
      [role="group"] {
        max-width: 100%;
      }
      .perseus_A5d7KcQj,
      .perseus-renderer,
      .paragraph,
      fieldset {
        min-width: 0;
      }
      fieldset {
        border: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      legend {
        margin-bottom: 8px !important;
        font-weight: 700 !important;
        color: #f8f1d8 !important;
        font-size: 1.02em !important;
        letter-spacing: 0.005em !important;
      }
      .perseus_A5d7KcQj .paragraph,
      li[role="listitem"] .paragraph,
      li[role="listitem"] div {
        color: #efe4c4 !important;
        font-weight: 500 !important;
      }
      li[role="listitem"]:has(button[aria-pressed="true"]) .paragraph,
      li[role="listitem"]:has(button[aria-pressed="true"]) div,
      li[role="listitem"]:has(button[aria-pressed="true"]) span {
        color: #f3ecd7 !important;
      }
      li[role="listitem"] button + div {
        padding-top: 1px;
      }
      li[role="listitem"][data-selected="true"],
      li[role="listitem"]:has(button[aria-pressed="true"]) {
        background: rgba(255, 248, 220, 0.06);
        border-color: rgba(245, 222, 179, 0.22);
      }
      mjx-container,
      .mathjax-wrapper,
      .mathjax-selectable {
        color: inherit !important;
        font: inherit !important;
      }
      .mathjax-wrapper[role="group"],
      .mathjax-wrapper[aria-label] {
        color: inherit !important;
        font-weight: inherit !important;
      }
      mjx-container {
        margin: 0 0.08em !important;
      }
      .mathjax-selectable svg {
        display: none !important;
      }
    </style>
  </head>
  <body>${questionHtml}</body>
</html>`;
}

function ConceptHeader({
  title,
  image,
  imagePosition = 'below',
  fontScale,
  compactMode,
}: {
  title: string;
  image?: string;
  imagePosition?: 'below' | 'right';
  fontScale: number;
  compactMode: boolean;
}) {
  const titleRef = useRef<HTMLDivElement>(null);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    if (title.includes('$')) {
      el.innerHTML = renderMixedContent(title);
    } else {
      el.textContent = title;
    }
  }, [title]);

  const showImage = !!image && !imgError;

  const headerStyle: React.CSSProperties = {
    paddingBottom: compactMode ? '0.5rem' : '0.75rem',
    borderBottom: '2px solid rgba(241,233,208,0.25)',
  };

  if (!showImage) {
    return (
      <div className="whiteboard-element text-center" style={headerStyle}>
        <div
          ref={titleRef}
          style={{
            fontSize: `${(compactMode ? 1.2 : 1.35) * fontScale}em`,
            lineHeight: compactMode ? '1.55' : '1.72',
          }}
        />
      </div>
    );
  }

  if (imagePosition === 'right') {
    return (
      <div
        className="whiteboard-element"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '24px',
          ...headerStyle,
        }}
      >
        <div
          ref={titleRef}
          style={{
            flex: 1,
            fontSize: `${(compactMode ? 1.2 : 1.35) * fontScale}em`,
            lineHeight: compactMode ? '1.55' : '1.72',
          }}
        />
        <img
          src={image}
          alt={title}
          onError={() => setImgError(true)}
          style={{
            maxHeight: compactMode ? '150px' : '200px',
            maxWidth: compactMode ? '220px' : '280px',
            objectFit: 'contain',
            borderRadius: '4px',
            flexShrink: 0,
          }}
        />
      </div>
    );
  }

  // Default: image below title
  return (
    <div className="whiteboard-element text-center" style={headerStyle}>
      <div
        ref={titleRef}
        style={{
          fontSize: `${(compactMode ? 1.2 : 1.35) * fontScale}em`,
          lineHeight: compactMode ? '1.55' : '1.72',
          marginBottom: compactMode ? '12px' : '16px',
        }}
      />
      <img
        src={image}
        alt={title}
        onError={() => setImgError(true)}
        style={{
          maxHeight: compactMode ? '180px' : '240px',
          maxWidth: compactMode ? '320px' : '400px',
          objectFit: 'contain',
          borderRadius: '4px',
          margin: '0 auto',
          display: 'block',
        }}
      />
    </div>
  );
}

function EntryItem({
  entry,
  prevType,
  isFirst,
  inHeadingSection,
  fontScale,
  compactMode,
}: {
  entry: BoardEntry;
  prevType?: BoardEntry['type'];
  isFirst: boolean;
  inHeadingSection?: boolean;
  fontScale: number;
  compactMode: boolean;
}) {
  const elRef = useRef<HTMLDivElement>(null);

  // Handwriting reveal: clip-path sweeps left-to-right
  useEffect(() => {
    const el = elRef.current;
    if (!el) return;
    // Start fully clipped (hidden)
    el.style.clipPath = 'inset(0 100% 0 0)';
    el.style.opacity = '1';
    // Duration scales with content length for natural pacing
    const len = (entry.renderedContent || entry.content).length;
    const durationMs = Math.min(Math.max(len * 30, 400), 1800);
    requestAnimationFrame(() => {
      el.style.transition = `clip-path ${durationMs}ms ease-out`;
      el.style.clipPath = 'inset(0 0% 0 0)';
    });
  }, [entry.content, entry.renderedContent]);

  // Render LaTeX via KaTeX
  const isLeftAligned = entry.type === 'concept' || entry.type === 'heading' || entry.type === 'subheading' || inHeadingSection;
  useEffect(() => {
    const contentEl = elRef.current?.querySelector('.board-entry-content');
    if (!contentEl) return;
    (contentEl as HTMLElement).style.whiteSpace = 'normal';

    if (entry.render === 'number_line' || entry.render === 'coordinate_plane' || entry.render === 'geometry') {
      return;
    }

    const text = normalizeLineBreaks(entry.renderedContent || entry.content);

    // If content has $ delimiters, parse mixed text+math
    if (text.includes('$')) {
      contentEl.innerHTML = renderMixedContent(text);
    } else if (text.includes('\\') || text.includes('^') || text.includes('_') || text.includes('{')) {
      // If the LaTeX contains \text{}, convert to mixed content (plain text + $...$)
      // so the browser can wrap at text boundaries instead of rendering one huge block.
      const mixed = textCommandsToMixed(text.trim());
      if (mixed !== null) {
        contentEl.innerHTML = renderMixedContent(mixed);
      } else {
        // Pure math expression — render as display math
        try {
          katex.render(text.trim(), contentEl as HTMLElement, {
            throwOnError: false,
            displayMode: true,
          });
        } catch {
          contentEl.textContent = stripLatexCommands(text);
        }
      }
    } else {
      (contentEl as HTMLElement).style.whiteSpace = 'pre-wrap';
      contentEl.textContent = text;
    }

    // KaTeX sets text-align:center on .katex and .katex-html inside display mode.
    // The CSS overrides in globals.css get stripped by the bundler, so force it inline.
    if (isLeftAligned) {
      contentEl.querySelectorAll<HTMLElement>('.katex, .katex-html').forEach((el) => {
        el.style.textAlign = 'left';
      });
    }

    // Shrink KaTeX display blocks that overflow the container
    shrinkOverflowingKatex(contentEl as HTMLElement);
  }, [entry.content, entry.renderedContent, entry.render, isLeftAligned]);

  const alignClass = isLeftAligned ? 'text-left' : 'text-center';

  // Variable top margin based on entry sequence
  let marginTop = 14; // default spacing between entries
  if (compactMode) {
    marginTop = 10;
  }
  if (!isFirst) {
    if (entry.type === 'heading') {
      // Heading gets large top margin to separate sections
      marginTop = compactMode ? 18 : 28;
    } else if (entry.type === 'subheading') {
      // Subheading gets moderate top margin (between heading's 28px and default 14px)
      marginTop = compactMode ? 8 : 12;
    } else if (prevType === 'heading' || prevType === 'subheading') {
      // Entry right after a heading or subheading — tight
      marginTop = compactMode ? 4 : 6;
    } else if (entry.type === 'concept' && prevType !== 'concept') {
      // Concept after a step/question — large gap to separate groups
      marginTop = compactMode ? 14 : 22;
    } else if (prevType === 'concept') {
      // Step/question right after a concept — tight to its label
      marginTop = compactMode ? 4 : 6;
    }
  }

  // Indent steps that follow a concept, or steps inside a heading section
  const indentAfterConcept = entry.type !== 'concept' && entry.type !== 'heading' && entry.type !== 'subheading' && prevType === 'concept';
  const indentInSection = inHeadingSection && entry.type === 'step';

  const typeStyle: React.CSSProperties =
    entry.type === 'question'
      ? {}
      : entry.type === 'concept'
        ? {
            paddingBottom: compactMode ? '2px' : '4px',
            opacity: 0.75,
            borderLeft: '3px solid rgba(241,233,208,0.4)',
            paddingLeft: compactMode ? '10px' : '12px',
          }
        : entry.type === 'heading'
          ? {
              ...(isFirst ? {} : { paddingTop: compactMode ? '10px' : '16px' }),
            }
          : entry.type === 'subheading'
            ? {} // plain text, no separator, no border
            : {};

  const baseFontSize =
    entry.type === 'concept' || entry.type === 'subheading'
      ? compactMode ? 0.96 : 1.05
      : entry.type === 'heading'
        ? compactMode ? 1.03 : 1.15
        : compactMode ? 1.16 : 1.35;
  const fontSize = `${baseFontSize * fontScale}em`;
  const paddingLeft = indentAfterConcept ? (compactMode ? '18px' : '24px') : indentInSection ? (compactMode ? '14px' : '20px') : undefined;

  return (
    <div
      ref={elRef}
      className={`whiteboard-element ${alignClass}`}
      style={{
        position: 'relative',
        fontSize,
        lineHeight: compactMode ? '1.55' : '1.72',
        marginTop,
        paddingLeft,
        ...typeStyle,
      }}
    >
      {entry.type === 'heading' && !isFirst && (
        <div style={{ width: '33%', borderTop: '1px solid rgba(241,233,208,0.2)', marginBottom: '8px' }} />
      )}
      <div className="board-entry-content">
        {entry.render === 'number_line' && entry.payload && (
          <NumberLineEntry payload={entry.payload as NumberLinePayload} />
        )}
        {entry.render === 'coordinate_plane' && entry.payload && (
          <CoordinatePlaneEntry payload={entry.payload as CoordinatePlanePayload} />
        )}
        {entry.render === 'geometry' && entry.payload && (
          <GeometryEntry payload={entry.payload as GeometryPayload} />
        )}
      </div>
    </div>
  );
}

const SHRINK_SCALES = [0.85, 0.7, 0.6] as const;

/**
 * Snap-shrink KaTeX elements that overflow their container.
 * Uses discrete scale tiers (0.85 → 0.7 → 0.6) for visual consistency.
 */
function shrinkOverflowingKatex(container: HTMLElement): void {
  const katexEls = container.querySelectorAll<HTMLElement>('.katex-display, .katex');
  katexEls.forEach((el) => {
    el.style.transform = '';
    el.style.transformOrigin = '';

    const parentWidth = container.clientWidth;
    const elWidth = el.scrollWidth;
    if (parentWidth <= 0 || elWidth <= parentWidth) return;

    // Pick the first scale tier that fits, or use the smallest
    const ratio = parentWidth / elWidth;
    const scale = SHRINK_SCALES.find((s) => ratio >= s) ?? SHRINK_SCALES[SHRINK_SCALES.length - 1];
    el.style.transform = `scale(${scale})`;
    el.style.transformOrigin = 'center top';
  });
}

/**
 * Convert pure LaTeX containing \text{} commands into mixed content format
 * (plain text + $...$) so it can wrap naturally.
 *
 * e.g. "0.5 \text{ hours} \times 60 \text{ minutes/hour} = 30 \text{ minutes}"
 *    → "$0.5$ hours $\times 60$ minutes/hour $= 30$ minutes"
 *
 * Returns null if the LaTeX has no \text{} commands (pure math, no conversion needed).
 */
function textCommandsToMixed(latex: string): string | null {
  if (!/\\text\s*\{/.test(latex)) return null;

  // Split on \text{...} — extract text content and keep math parts
  const parts: string[] = [];
  let remaining = latex;

  while (remaining.length > 0) {
    const match = remaining.match(/\\text\s*\{/);
    if (!match || match.index === undefined) {
      // No more \text{} — rest is math
      const math = remaining.trim();
      if (math) parts.push(`$${math}$`);
      break;
    }

    // Math before this \text{}
    const mathBefore = remaining.slice(0, match.index).trim();
    if (mathBefore) parts.push(`$${mathBefore}$`);

    // Find matching closing brace (handle one level of nesting)
    const braceStart = match.index + match[0].length;
    let depth = 1;
    let j = braceStart;
    while (j < remaining.length && depth > 0) {
      if (remaining[j] === '{') depth++;
      else if (remaining[j] === '}') depth--;
      j++;
    }

    const textContent = remaining.slice(braceStart, j - 1);
    parts.push(textContent);
    remaining = remaining.slice(j);
  }

  return parts.join('');
}

/**
 * Strip LaTeX commands from text so it's readable as plain text.
 * e.g. "\\textbf{Greatest Common Factor} \\\\ GCF is big" → "Greatest Common Factor  GCF is big"
 */
function stripLatexCommands(text: string): string {
  return text
    .replace(/\\textbf\{([^}]*)}/g, '$1')
    .replace(/\\textit\{([^}]*)}/g, '$1')
    .replace(/\\textrm\{([^}]*)}/g, '$1')
    .replace(/\\text\{([^}]*)}/g, '$1')
    .replace(/\\degree/g, '°')     // convert \degree to symbol
    .replace(/\\[a-zA-Z]+/g, '')   // remove remaining \commands
    .replace(/[{}]/g, '')           // remove stray braces
    .replace(/\s+/g, ' ')          // collapse whitespace
    .trim();
}
